/** @format */
import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import CustomInputExt from '../../../components/CustomInput/CustomInputExt';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { HeaderCheckBoxSelectListElement } from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import { SimpleSelect } from '../../../components/Adescom/SimpleSelect';
import { MvnoBlockErrorListInterface } from '../../../interfaces/platform/MvnoBlockErrorListInterface';

type MvnoErrorManagementDialogType = {
    closeCallback(): void;
    reloadCallback(): void;
    actionTypes: Array<HeaderCheckBoxSelectListElement>;
    open: boolean;
    editMessage: MvnoBlockErrorListInterface | null;
};

export const MvnoErrorManagementDialog: FunctionComponent<MvnoErrorManagementDialogType> = ({
    closeCallback,
    reloadCallback,
    open,
    editMessage,
    actionTypes,
}): ReactElement => {
    const dispatch = useDispatch();
    const [message, setMessage] = useState<string | null>(null);
    const [actionType, setActionType] = useState<number>(1);

    useEffect(() => {
        if (editMessage && message === null) {
            setMessage(editMessage.errorMessage);
            setActionType(editMessage.actionType);
        }
    }, [editMessage, message]);

    const saveEdit = (): void => {
        if (!editMessage) {
            return;
        }
        let itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(editMessage['@id']),
            'mvno_error_management_put',
            { errorMessage: message ?? '', actionType: actionType },
            (data: any): void => {
                setMessage(null);
                setActionType(1);
                reloadCallback();
                closeCallback();
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const createNewRecord = (): void => {
        let itemLoader = new ItemLoaders(dispatch);
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_block_error_lists')),
            'mvno_error_management_post',
            { errorMessage: message ?? '', actionType: actionType },
            (data: any): void => {
                setMessage(null);
                setActionType(1);
                reloadCallback();
                closeCallback();
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = event.target.value;
        setActionType(parseInt(selected));
    };

    return (
        <Dialog open={open} maxWidth="md" fullWidth={true}>
            <DialogTitle id="alert-dialog-title">
                {!editMessage && <I18n trans="prov:error_management.error_management_add" />}
                {editMessage && <I18n trans="prov:error_management.error_management_edit" />}
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            fullWidth={true}
                            stateName={'errorMessage'}
                            onChange={(event: React.FormEvent<HTMLInputElement>): void => {
                                setMessage(event.currentTarget.value);
                            }}
                            labelText={'prov:error_management.error_text'}
                            value={message ?? ''}
                        />
                        <br />
                    </GridItem>
                    <GridItem xs={6}>
                        <SimpleSelect
                            label="prov:error_management.action_type"
                            required={true}
                            fieldName="mvnoBlockVunctionId"
                            elements={actionTypes}
                            dispatchName={'no-dispatch'}
                            callback={onChangeSelectEvent}
                            selectedValue={actionType}
                        />
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={closeCallback} callbackParam={{}} label="general:common.close" size="sm" />
                <CustomTextButtom callback={editMessage ? saveEdit : createNewRecord} callbackParam={{}} label="general:common.save" size="sm" />
            </DialogActions>
        </Dialog>
    );
};
